import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import axios from "axios";
import { useSelector } from "react-redux";
import GameCard from "./GameCard";
import GameLaunchOverlay from "./GameLaunchOverlay";
import GamePaginationControls from "./GamePaginationControls";
import { buildGameStartPayload } from "../utils/gameStartPayload";
import {
  createRestrictionState,
  getBlockedTypeMessage,
} from "../utils/gameRestrictions";
import {
  createProviderGamesPagination,
  DEFAULT_PROVIDER_GAMES_LIMIT,
  fetchProviderGames,
} from "../utils/providerGames";
import { resolveProviderImage } from "../utils/providerImage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const remoteProviderPrefixes = [
  "bs",
  "ng",
  "aws",
  "swg",
  "km",
  "fc",
  "mg",
  "dgs",
  "gamatron",
  "ep",
  "jl",
  "sn",
  "pp",
  "kg",
  "netent",
  "joker",
  "bpg",
  "bng",
  "hab",
  "kgl",
  "rlx",
  "ygg",
  "red",
  "qs",
  "ids",
  "tk",
  "ds",
  "nlc",
  "ga",
  "png",
  "pug",
  "fng",
  "nge",
  "1x2",
  "hak",
  "waz",
  "elk",
  "prs",
  "dgs",
];

const HomeSlotSection = ({ gameList }) => {
  const [selectedSlotProvider, setSelectedSlotProvider] = useState(null);
  const [slotProviderGames, setSlotProviderGames] = useState([]);
  const [slotGamesPagination, setSlotGamesPagination] = useState(() =>
    createProviderGamesPagination(),
  );
  const [isLoadingSlotGames, setIsLoadingSlotGames] = useState(false);
  const [slotGamesError, setSlotGamesError] = useState(null);
  const [showSlotSearch, setShowSlotSearch] = useState(false);
  const [slotQuery, setSlotQuery] = useState("");
  const [gameStartLoading, setGameStartLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  // Redux user info for permission checks
  const { singleUsers } = useSelector((state) => state.user);
  const currentUser = singleUsers && singleUsers.length ? singleUsers[0] : null;
  const restrictionState = createRestrictionState(currentUser);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1200);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadSlotProviderGames = async (provider, page = 1) => {
    const providerCode = (
      provider?.game ||
      provider?.productId ||
      ""
    ).toLowerCase();
    const providerName = (
      provider?.name ||
      provider?.productName ||
      ""
    ).toLowerCase();
    const shouldUseRemote =
      remoteProviderPrefixes.includes(providerCode) ||
      remoteProviderPrefixes.includes(providerName);

    if (!shouldUseRemote) {
      setSelectedSlotProvider(null);
      setIsLoadingSlotGames(false);
      setSlotGamesError(null);
      setSlotProviderGames([]);
      setShowSlotSearch(false);
      setSlotQuery("");
      return getGameLink(provider);
    }

    setSelectedSlotProvider(provider);
    setIsLoadingSlotGames(true);
    setSlotGamesError(null);
    setSlotProviderGames([]);
    setSlotGamesPagination((currentPagination) => ({
      ...currentPagination,
      page,
    }));
    setShowSlotSearch(false);
    setSlotQuery("");

    try {
      const result = await fetchProviderGames({
        apiBaseUrl: API_BASE_URL,
        provider,
        categoryCode: "SLOTS",
        page,
        limit: slotGamesPagination.limit || DEFAULT_PROVIDER_GAMES_LIMIT,
      });
      setSlotProviderGames(result.games);
      setSlotGamesPagination(result.pagination);
    } catch (err) {
      console.error(err);
      setSlotGamesError("ไม่สามารถโหลดรายการเกมได้");
      setSlotProviderGames([]);
      setSlotGamesPagination(
        createProviderGamesPagination(
          slotGamesPagination.limit || DEFAULT_PROVIDER_GAMES_LIMIT,
        ),
      );
    } finally {
      setIsLoadingSlotGames(false);
    }
  };

  const backToSlotProviders = () => {
    setSelectedSlotProvider(null);
    setSlotProviderGames([]);
    setSlotGamesPagination(createProviderGamesPagination());
    setSlotGamesError(null);
    setShowSlotSearch(false);
    setSlotQuery("");
  };

  const playerBalance = useSelector((state) => state.user.playerBalance);

  const getGameLink = async (request) => {
    setGameStartLoading(true);
    let userId = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        userId = parsed?.id || parsed?.user?.id || parsed?.UserID || null;
      }
    } catch {}

    const data = { id: userId };
    if (selectedSlotProvider) {
      data.provider =
        selectedSlotProvider?.productId ||
        selectedSlotProvider?.game ||
        selectedSlotProvider?.name;
      data.gamecode =
        request?.code ||
        request?.gameCode ||
        request?.gamecode ||
        request?.game;
    } else {
      const fallbackProvider =
        request?.productId ||
        request?.game ||
        request?.provider ||
        request?.name;
      data.provider = fallbackProvider;
      data.gamecode =
        request?.code ||
        request?.gameCode ||
        request?.gamecode ||
        fallbackProvider;
    }

    try {
      const res = await axios({
        method: "POST",
        url: `${API_BASE_URL}/gamelogin/gamestart`,
        data: {
          data: buildGameStartPayload({ data, playerBalance }),
        },
        maxBodyLength: Infinity,
      });
      if (res.data) {
        window.location.href = res.data;
      }
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการเข้าสู่เกมส์");
    } finally {
      setGameStartLoading(false);
    }
  };

  const filteredSlotProviderGames = useMemo(() => {
    if (!slotQuery) return slotProviderGames;
    const q = slotQuery.toLowerCase();
    return (slotProviderGames || []).filter((g) => {
      const name = (g.gameName || g.name || g.title || "").toLowerCase();
      return name.includes(q);
    });
  }, [slotProviderGames, slotQuery]);

  const handleSlotGamesPageChange = (page) => {
    if (
      !selectedSlotProvider ||
      page === slotGamesPagination.page ||
      page < 1
    ) {
      return;
    }

    loadSlotProviderGames(selectedSlotProvider, page);
  };

  // Permission helpers
  const isTypeAllowedFor = restrictionState.isTypeAllowed;
  const isProviderAllowed = restrictionState.isProviderAllowed;

  const handleProviderSelect = (provider) => {
    // If not logged in, alert and don't load games
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }

    if (!isTypeAllowedFor("slot")) {
      showToast(getBlockedTypeMessage("slot"), "warning");
      return;
    }
    if (!isProviderAllowed(provider)) {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }
    loadSlotProviderGames(provider);
  };

  const renderProviderCard = (provider, index) => {
    if (!provider) return null;
    const src = resolveProviderImage(provider);
    return (
      <div
        key={provider.productId || provider.game}
        style={{
          backgroundColor: "#2d2d2d",
          borderRadius: 12,
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.3s ease",
          border: "1px solid #404040",
          position: "relative",
          overflow: "hidden",
          gridColumn: index === 0 ? "span 2" : "auto",
          gridRow: index === 0 ? "span 2" : "auto",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.05)";
          e.currentTarget.style.boxShadow =
            "0 8px 25px var(--primaryColor,--primaryColor)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = "none";
        }}
        onClick={() => handleProviderSelect(provider)}
      >
        <img
          src={src}
          alt={provider.productName || provider.name}
          style={{
            width: "100%",
            objectFit: "contain",
            borderRadius: 8,
            height: index === 0 ? "100%" : "auto",
          }}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
        <h3
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            color: "white",
            fontSize: 14,
            margin: 0,
            padding: "4px 12px",
            fontWeight: 600,
            backgroundColor: "rgba(0,0,0,0.7)",
            borderRadius: "0 0 8px 8px",
          }}
        >
          {provider.productName || provider.name}
        </h3>
      </div>
    );
  };

  const providers = gameList?.slots || [];

  return (
    <div style={{ marginTop: 40 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <h1
          style={{
            color: "#0e6c5e",
            margin: 0,
            fontSize: 32,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Icon
            icon="mdi:slot-machine"
            width={32}
            height={32}
            style={{ color: "#0e6c5e" }}
          />
          {selectedSlotProvider ? "เกมจากผู้ให้บริการ" : "สล๊อต"}
        </h1>
        {selectedSlotProvider ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flex: 1,
            }}
          >
            <button
              onClick={backToSlotProviders}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "color-mix(in srgb, #0e6c5e 10%, transparent)",
                border: "1.5px solid #0e6c5e",
                color: "#0e6c5e",
                borderRadius: 25,
                padding: "8px 18px 8px 12px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                fontSize: 14,
                fontWeight: 600,
                transition: "all 0.25s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#0e6c5e";
                e.currentTarget.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "color-mix(in srgb, #0e6c5e 10%, transparent)";
                e.currentTarget.style.color = "#0e6c5e";
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              กลับไปหน้าผู้ให้บริการ
            </button>
            {showSlotSearch ? (
              <div
                style={{ position: "relative", flex: "0 1 300px", minWidth: 0 }}
              >
                <input
                  autoFocus
                  value={slotQuery}
                  onChange={(e) => setSlotQuery(e.target.value)}
                  placeholder="ค้นหาเกม..."
                  style={{
                    width: "100%",
                    padding: "9px 36px 9px 14px",
                    borderRadius: 24,
                    border: "1.5px solid #0e6c5e",
                    outline: "none",
                    background: "#ffffff",
                    color: "#1f2937",
                    fontWeight: 600,
                    fontSize: 14,
                    boxSizing: "border-box",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowSlotSearch(false);
                      setSlotQuery("");
                    }
                  }}
                />
                <button
                  onClick={() => {
                    setShowSlotSearch(false);
                    setSlotQuery("");
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "#999",
                    cursor: "pointer",
                    padding: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowSlotSearch(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background:
                    "linear-gradient(135deg, #0e6c5e 0%, #0b5b50 100%)",
                  border: "none",
                  borderRadius: 25,
                  padding: "8px 18px 8px 14px",
                  cursor: "pointer",
                  color: "white",
                  fontSize: 14,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 8px rgba(14, 108, 94, 0.25)",
                  transition: "all 0.25s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.85";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 14px rgba(14, 108, 94, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(14, 108, 94, 0.25)";
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 21L16.514 16.506M19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                ค้นหา
              </button>
            )}
          </div>
        ) : null}
      </div>
      <hr style={{ border: "2px solid #b7e3d8", marginBottom: 20 }} />

      {!selectedSlotProvider ? (
        <div
          className="slots-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 16,
          }}
        >
          {providers
            .slice(0, 9)
            .map((provider, index) => renderProviderCard(provider, index))}
        </div>
      ) : (
        <div>
          <div style={{ color: "#000000", marginBottom: 12 }}>
            ผู้ให้บริการ:{" "}
            {selectedSlotProvider?.productName ||
              selectedSlotProvider?.name ||
              "-"}
          </div>
          {isLoadingSlotGames && (
            <div style={{ color: "#aaa" }}>กำลังโหลดรายการเกม...</div>
          )}
          {slotGamesError && (
            <div style={{ color: "#ef4444" }}>{slotGamesError}</div>
          )}
          {!isLoadingSlotGames && !slotGamesError && (
            <>
              <div
                className="slots-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  gap: 16,
                }}
              >
                {filteredSlotProviderGames?.length ? (
                  filteredSlotProviderGames.map((g) => (
                    <GameCard
                      key={
                        g.gameId ||
                        g.id ||
                        `${
                          g.providerId ||
                          selectedSlotProvider?.productId ||
                          selectedSlotProvider?.game
                        }-${g.code || g.gameCode || g.gamecode || g.name}`
                      }
                      game={g}
                      onClick={() => getGameLink(g)}
                    />
                  ))
                ) : (
                  <div style={{ color: "#aaa" }}>
                    ไม่พบเกมสำหรับผู้ให้บริการนี้
                  </div>
                )}
              </div>
              <GamePaginationControls
                pagination={slotGamesPagination}
                disabled={isLoadingSlotGames}
                onPageChange={handleSlotGamesPageChange}
              />
            </>
          )}
        </div>
      )}

      {gameStartLoading && <GameLaunchOverlay />}

      {/* Toast Notification */}
      {toast.show && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            backgroundColor: "#FEF3C7",
            border: "2px solid #F59E0B",
            borderRadius: "12px",
            padding: "16px 20px",
            maxWidth: "400px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <span style={{ fontSize: "20px" }}>⚠️</span>
          <span style={{ color: "#92400E", fontWeight: "500", flex: 1 }}>
            {toast.message}
          </span>
          <button
            onClick={hideToast}
            style={{
              background: "none",
              border: "none",
              fontSize: "18px",
              cursor: "pointer",
              color: "#92400E",
              padding: "0",
              marginLeft: "8px",
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default HomeSlotSection;
